import React, { useEffect, useRef } from 'react';
import { FormRenderProps } from 'react-final-form';
import {
  clearPayingWithApplePay,
  createApplePayPaymentRequest,
  IApplePayLineItems,
  prepareFormForApplePay
} from '../../utils/applePay';

type ApplePayButtonProps = {
  merchantIdentifier: string;
  description: string;
  lineItems: IApplePayLineItems;
  form: FormRenderProps;
  onSubmit(paymentRequest: PaymentRequest): any;
}

const ApplePayButton: React.FC<ApplePayButtonProps> = (props) => {
  const buttonRef = useRef<HTMLElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  const canMakePayments = window.PaymentRequest
    && (window as any).ApplePaySession
    && (window as any).ApplePaySession.canMakePayments();

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) {
      return undefined;
    }

    // React 16 does not reliably attach onClick to custom elements. Use a native listener.
    const handleClick = () => {
      const currentProps = propsRef.current;
      const formApi = currentProps.form.form;
      prepareFormForApplePay(formApi);

      // Only personal info and agreement should block Apple Pay, not empty CC fields.
      if (formApi.getState().invalid) {
        clearPayingWithApplePay(formApi);
        formApi.submit();
        return;
      }

      // PaymentRequest.show() must run in this user-gesture turn.
      currentProps.onSubmit(
        createApplePayPaymentRequest(
          currentProps.merchantIdentifier,
          currentProps.description,
          currentProps.lineItems
        )
      );
    };

    button.addEventListener('click', handleClick);
    return () => button.removeEventListener('click', handleClick);
  }, [canMakePayments]);

  if (!canMakePayments) {
    return null;
  }

  // Official Apple Pay JS SDK button. CSS -webkit-appearance buttons do not work in Chrome.
  return (
    <div className="apple-pay-button-container mb-3">
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type="pay"
        locale="en-US"
      />
    </div>
  );
}

export default ApplePayButton;
